import { useMemo, useState } from 'react';
import { useBrandsOverviewQuery } from '../../queries/brandMetrics';
import { formatMetric } from './panelConfig';
import SortHeader from './components/SortHeader';
import BrandLogo from './components/BrandLogo';

const fmtMoney = (v, cur) => (v == null ? '—' : `${cur || ''} ${formatMetric(v, 'number')}`.trim());

// Formato condicional sobre la celda completa (fondo), estilo Metabase.
const pctCell = (pct) => {
  if (pct == null) return 'text-gray-400';
  if (pct >= 100) return 'bg-emerald-100 text-emerald-800 font-medium';
  if (pct >= 80) return 'bg-amber-100 text-amber-800 font-medium';
  return 'bg-red-100 text-red-800 font-medium';
};

// Semáforo de % de marketing: real vs objetivo, banda de ±5% relativo al objetivo.
// 🟢 en objetivo · 🟡 gastando de menos · 🔴 pasado de budget.
const MKT_TOLERANCE = 0.05;

const mktStatus = (real, target) => {
  if (real == null || target == null) return null;
  if (real < target * (1 - MKT_TOLERANCE)) return 'under';
  if (real > target * (1 + MKT_TOLERANCE)) return 'over';
  return 'on';
};

const MKT_DOT = {
  on: { cls: 'bg-emerald-500', label: 'En objetivo' },
  under: { cls: 'bg-amber-500', label: 'Por debajo del objetivo (gastando de menos)' },
  over: { cls: 'bg-red-500', label: 'Por encima del objetivo (pasado de budget)' },
};

const MktDot = ({ real, target }) => {
  const status = mktStatus(real, target);
  if (!status) return null;
  const { cls, label } = MKT_DOT[status];
  return (
    <span
      className={`inline-block h-2 w-2 shrink-0 rounded-full ${cls}`}
      title={`${label} · objetivo ${formatMetric(target, 'number')}%`}
    />
  );
};

const COLUMNS = [
  { key: 'name', label: 'Cliente', align: 'left' },
  { key: 'target', label: 'Target', align: 'right' },
  { key: 'achieved', label: 'Alcanzado', align: 'right' },
  { key: 'pct_vs_target', label: '% vs Target', align: 'right' },
  { key: 'orders', label: 'Órd./Leads', align: 'right' },
  { key: 'avg_ticket', label: 'Ticket', align: 'right' },
  { key: 'conversion_rate', label: 'CR %', align: 'right' },
  { key: 'sessions', label: 'Sesiones', align: 'right' },
  { key: 'spend', label: 'Spend', align: 'right' },
  { key: 'mkt_pct', label: '% Mkt', align: 'right' },
];

const numCell = 'px-4 py-3 text-right tabular-nums text-gray-700';

/**
 * Tabla de pacing de todas las marcas (home del panel admin), misma línea
 * estética que la sección de campañas. Orden por cualquier columna (desc → asc,
 * nulls al final); click en una fila abre el panel de esa marca.
 */
export default function BrandsOverviewTable({ start, end, onSelectBrand }) {
  const { data: rows = [], isLoading, isError } = useBrandsOverviewQuery(start, end);
  const [sort, setSort] = useState({ key: 'pct_vs_target', dir: 'desc' });

  const sortedRows = useMemo(() => {
    if (!sort.key) return rows;
    const mul = sort.dir === 'asc' ? 1 : -1;
    return [...rows].sort((a, b) => {
      const va = a[sort.key];
      const vb = b[sort.key];
      if (va == null && vb == null) return 0;
      if (va == null) return 1; // nulls siempre al final
      if (vb == null) return -1;
      if (typeof va === 'string') return va.localeCompare(vb) * mul;
      return (va - vb) * mul;
    });
  }, [rows, sort]);

  const onSort = (key) =>
    setSort((s) => ({ key, dir: s.key === key && s.dir === 'desc' ? 'asc' : 'desc' }));

  if (isLoading) return <p className="py-12 text-center text-sm text-gray-500">Cargando marcas…</p>;
  if (isError) return <p className="py-12 text-center text-sm text-red-600">No se pudo cargar el resumen.</p>;

  return (
    <div className="overflow-hidden rounded-xl border border-gray-200 bg-white">
      <div className="overflow-x-auto">
        <table className="w-full min-w-[820px] border-collapse text-sm">
          <thead>
            <tr className="border-b border-gray-200 text-xs uppercase tracking-wide text-gray-500">
              {COLUMNS.map((c) => (
                <SortHeader key={c.key} sortKey={c.key} sort={sort} onSort={onSort} align={c.align}>
                  {c.label}
                </SortHeader>
              ))}
            </tr>
          </thead>
          <tbody>
            {sortedRows.map((r) => (
              <tr
                key={r.brand_id}
                className="cursor-pointer border-b border-gray-100 last:border-0 hover:bg-gray-50"
                onClick={() => onSelectBrand(r.brand_id)}
              >
                <td className="px-4 py-3">
                  <div className="flex items-center gap-2.5">
                    <BrandLogo name={r.name} logoUrl={r.logo_url} />
                    <span className="font-medium text-gray-900">{r.name}</span>
                    <span className="rounded-md bg-gray-100 px-2 py-0.5 text-xs font-medium uppercase tracking-wide text-gray-500">
                      {r.vertical === 'lead_gen' ? 'leads' : 'ecom'}
                    </span>
                  </div>
                </td>
                <td className={numCell}>
                  {r.vertical === 'lead_gen'
                    ? r.target == null
                      ? '—'
                      : `${Math.round(r.target)} leads`
                    : fmtMoney(r.target, r.currency)}
                </td>
                <td className="px-4 py-3 text-right tabular-nums text-gray-900">
                  {r.vertical === 'lead_gen'
                    ? r.achieved == null
                      ? '—'
                      : `${r.achieved} leads`
                    : fmtMoney(r.achieved, r.currency)}
                </td>
                <td className={`px-4 py-3 text-right tabular-nums ${pctCell(r.pct_vs_target)}`}>
                  {r.pct_vs_target == null ? '—' : `${formatMetric(r.pct_vs_target, 'number')}%`}
                </td>
                <td className={numCell}>{r.orders ?? '—'}</td>
                <td className={numCell}>
                  {r.avg_ticket == null ? '—' : `$ ${formatMetric(r.avg_ticket, 'number')}`}
                </td>
                <td className={numCell}>
                  {r.conversion_rate == null ? '—' : formatMetric(r.conversion_rate, 'ratio').replace('x', '%')}
                </td>
                <td className={numCell}>{r.sessions == null ? '—' : formatMetric(r.sessions, 'number')}</td>
                <td className={numCell}>{fmtMoney(r.spend, r.currency)}</td>
                <td className={numCell}>
                  {r.mkt_pct == null ? '—' : (
                    <span className="inline-flex items-center justify-end gap-1.5">
                      <MktDot real={r.mkt_pct} target={r.mkt_pct_target} />
                      {`${formatMetric(r.mkt_pct, 'ratio').replace('x', '')}%`}
                    </span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {rows.length === 0 && (
        <p className="py-8 text-center text-sm text-gray-500">Sin marcas configuradas.</p>
      )}
    </div>
  );
}
